import { FC, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { DotSpinner } from '@crono-one/design-system';
import { toast } from '@crono-one/design-system/side-effects';

import PATH from 'crono-fe-common/constants/path';
import CronoError from 'crono-fe-common/types/error';
import { useAuth } from 'context/auth';
import useDynamicsEnvironments from 'hooks/services/dynamics/useDynamicsEnvironments';
import useDynamicsAuthCallback from 'hooks/services/dynamics/useDynamicsAuthCallback';
import {
  buildDynamicsEnvironmentLoginUrl,
  clearDynamicsConnectError,
  clearDynamicsOauthFlow,
  getDynamicsOauthFlow,
  isDynamicsOauthStateValid,
  saveDynamicsEnvironmentsToPick,
  storeDynamicsConnectError,
} from 'hooks/services/dynamics/dynamicsOauthState';
import {
  DYNAMICS_CONNECT_ERRORS,
  getDynamicsBackendErrorMessage,
  getDynamicsMicrosoftErrorMessage,
} from 'utils/dynamicsConnectErrors';

/**
 * One callback route for both Microsoft sign-ins. The step comes from
 * sessionStorage, never from the URL:
 * - `environments` exchanges the code for the environment list, then connects
 *   the only one, or hands the list to the picker on the page the user started
 *   from;
 * - `connect` exchanges the code for the chosen environment.
 *
 * Every code is posted once (codes are single-use) and every exit navigates
 * with `replace`, so Back cannot remount the page and re-post a consumed code.
 * Failures are stored for the banner on the Dynamics card, not toasted.
 */
const DynamicsLoginHandler: FC = () => {
  const { user, refreshUser } = useAuth();
  const { search } = useLocation();
  const navigate = useNavigate();
  const isRedirectHandledRef = useRef(false);

  const { mutate: getEnvironments } = useDynamicsEnvironments();
  const { mutate: connectEnvironment } = useDynamicsAuthCallback();

  useEffect(() => {
    // Both exchanges resolve the Crono account from the session.
    if (!user || isRedirectHandledRef.current) return;
    isRedirectHandledRef.current = true;

    const flow = getDynamicsOauthFlow();
    const returnPath = flow?.returnPath ?? PATH.SETTINGS.INTEGRATIONS.MAIN;
    const leave = () => navigate(returnPath, { replace: true });
    const fail = (message: string) => {
      clearDynamicsOauthFlow();
      storeDynamicsConnectError(message);
      leave();
    };

    const {
      code,
      state,
      error,
      error_description: errorDescription,
    } = queryString.parse(search);

    // A callback that fails the state check leaves the flow alone, or a forged
    // redirect would cancel the legitimate one still pending in this tab.
    if (!isDynamicsOauthStateValid(flow, state)) {
      toast.error('Microsoft returned an unexpected state');
      leave();
      return;
    }

    if (typeof error === 'string') {
      fail(
        getDynamicsMicrosoftErrorMessage(
          error,
          typeof errorDescription === 'string' ? errorDescription : null,
        ),
      );
      return;
    }

    if (typeof code !== 'string' || code === '') {
      fail(DYNAMICS_CONNECT_ERRORS.generic);
      return;
    }

    const onError = (exchangeError: CronoError) =>
      fail(getDynamicsBackendErrorMessage(exchangeError));

    if (flow.step === 'environments') {
      getEnvironments(
        { code },
        {
          onSuccess: (response) => {
            const environments = (response.data.data ?? []).filter(
              (environment) => environment.organizationId && environment.url,
            );

            if (environments.length === 0) {
              fail(DYNAMICS_CONNECT_ERRORS.noEnvironment);
            } else if (environments.length === 1) {
              window.location.replace(
                buildDynamicsEnvironmentLoginUrl(returnPath, environments[0]),
              );
            } else {
              saveDynamicsEnvironmentsToPick(returnPath, environments);
              leave();
            }
          },
          onError,
        },
      );
      return;
    }

    const { environment } = flow;
    connectEnvironment(
      {
        code,
        organizationId: environment.organizationId ?? '',
        environmentUrl: environment.url ?? '',
        environmentName: environment.friendlyName,
        tenantId: environment.tenantId,
      },
      {
        onSuccess: async () => {
          clearDynamicsOauthFlow();
          clearDynamicsConnectError();
          try {
            await refreshUser();
          } catch {
            // The connection is saved, so a failing refresh is not a reason to
            // strand the user on a route that renders nothing but a spinner.
          }
          leave();
        },
        onError,
      },
    );
  }, [
    user,
    search,
    navigate,
    refreshUser,
    getEnvironments,
    connectEnvironment,
  ]);

  return (
    <div className="flex h-full w-full items-center justify-center">
      <DotSpinner />
    </div>
  );
};

export default DynamicsLoginHandler;
